/**
 * Shared TypeScript interfaces for the ESC/POS thermal printer module.
 */

// ─── Printer configuration ──────────────────────────────────────────────────

export type PrinterConnectionType = 'usb' | 'network' | 'browser';
export type PaperWidth = 58 | 80;
export type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'error';

export interface PrinterConfig {
  connectionType: PrinterConnectionType;
  paperWidth: PaperWidth;
  printerName: string | null;
  /** IP address for network printers */
  printerIp: string | null;
  /** TCP port for network printers (default 9100) */
  printerPort: number;
  autoPrint: boolean;
  autoCut: boolean;
  copies: number;
  kickCashDrawer: boolean;
}

export const DEFAULT_PRINTER_CONFIG: PrinterConfig = {
  connectionType: 'browser',
  paperWidth: 80,
  printerName: null,
  printerIp: null,
  printerPort: 9100,
  autoPrint: false,
  autoCut: true,
  copies: 1,
  kickCashDrawer: false,
};

// ─── Printer device info ────────────────────────────────────────────────────

export interface PrinterDeviceInfo {
  name: string;
  type: PrinterConnectionType;
  vendorId?: number;
  productId?: number;
  ipAddress?: string;
  port?: number;
}

// ─── Print job ──────────────────────────────────────────────────────────────

export type PrintJobType = 'receipt' | 'duplicate' | 'kot' | 'kot-reprint' | 'test';
export type PrintJobStatus = 'queued' | 'printing' | 'completed' | 'failed';

export interface PrintJob {
  id: string;
  type: PrintJobType;
  status: PrintJobStatus;
  data: Uint8Array;
  copies: number;
  createdAt: string;
  error?: string;
  /** Bill ID or order ID for reference */
  referenceId?: string;
}

export interface PrintResult {
  success: boolean;
  error?: string;
  jobId: string;
}

// ─── Receipt data (mirrors backend print.service.ts structure) ──────────────

export interface ReceiptBillItem {
  product_name: string;
  product_sku: string;
  hsn_code?: string;
  quantity: number;
  unit: string;
  /** in paise */
  unit_price: number;
  gst_rate: number;
  /** in paise */
  line_discount: number;
  /** in paise */
  line_tax: number;
  /** in paise */
  line_total: number;
}

export interface ReceiptBillData {
  id: string;
  bill_number: string;
  status: string;
  created_at: string;
  /** in paise */
  subtotal: number;
  /** in paise */
  discount_total: number;
  /** in paise */
  tax_total: number;
  /** in paise */
  grand_total: number;
  /** Cash rounding in paise (amount settled − grand_total); 0 / absent when exact. */
  rounding_adjustment?: number;
  payment_method: string | null;
  /** in paise */
  amount_paid: number;
  /** in paise */
  change_due: number;
  /** The tender rows; more than one prints each part (PS1-001). */
  payments?: { payment_method: string; amount: number }[];
  /**
   * Customer attached to this bill, if any. When omitted/null the receipt
   * prints "Customer: Walk-in" — used for explicit walk-in sales.
   */
  customer_id?: string | null;
  customer_name: string | null;
  customer_phone: string | null;
  /** Current loyalty balance after this sale; null when walk-in or unknown. */
  customer_loyalty_balance?: number | null;
  /** Points earned by THIS bill (0 when none). */
  customer_loyalty_earned?: number | null;
  /** Points redeemed against THIS bill (0 when none). */
  customer_loyalty_used?: number | null;
  notes: string | null;
  items: ReceiptBillItem[];
  /**
   * India GST: the tax type the bill was saved with (gst-parked 080) — the
   * slip prints CGST + SGST per rate (INTRA, or absent) or IGST (INTER).
   */
  supply_type?: 'INTRA' | 'INTER' | 'NONE' | null;
  /** T04 — set on a credit note: the bill it returns against. */
  return_against?: string | null;
  original_bill_number?: string | null;
  /** Store-credit code a credit note issued; printed with a QR of the code. */
  redemption_code?: string | null;
  /** T19: the till's OFL number this (synced) bill was sold under offline — printed as "Offline ref". */
  offline_ref?: string | null;
  /** Signed verify link printed as the QR when there is no code (cash refund). */
  credit_note_verify_url?: string | null;
}

export interface ReceiptTenantData {
  business_name: string;
  gstin: string | null;
  phone: string | null;
  receipt_footer: string | null;
}

export interface ReceiptLocationData {
  name: string;
  address: string | null;
  city: string | null;
  state: string | null;
  pincode: string | null;
  phone: string | null;
}

export interface ReceiptOptions {
  paperWidth: PaperWidth;
  printQr: boolean;
  qrUrl?: string;
}

/**
 * Regime-aware tax vocabulary embedded in the print payload.
 *
 * The ESC/POS builder has no React context — the caller (which DOES have
 * `useTaxRegime()`) must hand the resolved labels in via this struct so the
 * thermal receipt uses tenant-correct terminology (GST/VAT, GSTIN/TRN, HSN
 * visibility). Subset of `TaxRegimePresentation` in `@bbpos/ui` — kept local
 * here to avoid the printer module depending on the React UI package.
 *
 * Omitting it defaults to {@link DEFAULT_INDIA_TAX_PRESENTATION}, so every
 * pre-multi-currency call site is byte-identical to today.
 */
export interface ReceiptTaxPresentation {
  /** Short tax name: `GST` (India) · `VAT` (GCC) · `Tax` (no-VAT). */
  readonly taxName: 'GST' | 'VAT' | 'Tax';
  /**
   * Statutory tax-identifier label (GSTIN/TRN/VATIN/VAT No.). `null` for
   * regimes with no statutory id (Kuwait/Qatar/NO_VAT) — the GSTIN line is
   * suppressed entirely in that case.
   */
  readonly taxIdLabel: string | null;
  /**
   * Per-rate split column labels. India splits into CGST/SGST/IGST; every
   * GCC regime is a single VAT line ⇒ `null`. With split labels the receipt
   * prints CGST / SGST or IGST per rate (gst-parked 080).
   */
  readonly splitLabels: readonly ['CGST', 'SGST', 'IGST'] | null;
  /** India-only HSN/SAC code surface — hidden for every GCC regime. */
  readonly showHsn: boolean;
}

/**
 * India / GST regime defaults. Used as the fallback whenever a caller omits
 * the tax payload, so every existing INR receipt stays byte-identical.
 */
export const DEFAULT_INDIA_TAX_PRESENTATION: ReceiptTaxPresentation = {
  taxName: 'GST',
  taxIdLabel: 'GSTIN',
  splitLabels: ['CGST', 'SGST', 'IGST'],
  showHsn: true,
};

// ─── KOT data (restaurant vertical) ────────────────────────────────────────

export interface KotItem {
  name: string;
  quantity: number;
  modifiers?: string[];
  special_instructions?: string;
}

export interface KotData {
  order_number: string;
  order_type: 'DINE_IN' | 'TAKEAWAY' | 'DELIVERY';
  table_number?: string;
  station: string;
  items: KotItem[];
  is_rush: boolean;
  is_reprint: boolean;
  created_at: string;
}

// ─── Transport interface ────────────────────────────────────────────────────

export interface PrinterTransport {
  type: PrinterConnectionType;
  connect(): Promise<void>;
  disconnect(): Promise<void>;
  write(data: Uint8Array): Promise<void>;
  isConnected(): boolean;
  getDeviceInfo(): PrinterDeviceInfo | null;
}
